import { useSelfRatablePairings } from '../../hooks/useSelfRatablePairings';
import { useSelfRatableProductsFeedbackModal } from '../../hooks/useSelfRatableProductsFeedbackModal';

type UseRatableItemsReturn = {
  pairings: RatablePairingItem[];
  products: RatableProductItem[];
  isPending: boolean;
  isError: boolean;
};

export type RatablePairingItem = {
  pairingId: number;
  bundleId: number | null;
  name: string;
  ingredients: {
    brand_name: string | null;
    card_image_url: string | null;
    is_breakfast: boolean;
    description: string;
    is_fruit: boolean;
    is_snack: boolean;
    is_sweet: boolean;
    id: number;
    name: string;
    nutrition_image_url: string | null;
    rating: string;
    short_name: string | null;
    sku: string | null;
    slug: string;
    small_image_url: string | null;
    small_image2_url: string | null;
    thumbnail_image_url: string | null;
  }[];
  type: RatableItemType.Pairing;
  orderNumber: number;
  featuredImgUrl: string | null;
};

export type RatableProductItem = {
  productId: number;
  name: string;
  type: RatableItemType.Product;
  orderNumber: number;
  cardImageUrl: string | null;
  thumbImageUrl: string | null;
};

export enum RatableItemType {
  Pairing,
  Product,
}

type UseRatableItemsProps = {
  recentOrderCount?: number;
};

export function useRatableItems(
  props?: UseRatableItemsProps
): UseRatableItemsReturn {
  const { recentOrderCount } = props || {};

  const {
    isPending: ratablePairingsIsPending,
    isError: ratablePairingsIsError,
    data: ratablePairingsData,
  } = useSelfRatablePairings({
    queryParams: {
      feedback_modal_eligible: true,
      bypass_viewed: true,
      recent_order_count: recentOrderCount,
    },
  });

  let pairings: UseRatableItemsReturn['pairings'] = [];

  if (ratablePairingsData?.results) {
    pairings = ratablePairingsData.results.map((pairing) => {
      return {
        pairingId: pairing.id,
        bundleId: pairing.bundle,
        name: pairing.name,
        ingredients: pairing.ingredients,
        type: RatableItemType.Pairing,
        orderNumber: pairing.order,
        featuredImgUrl: pairing.featured_img_url,
      };
    });
  }

  const {
    isPending: ratableProductsIsPending,
    isError: ratableProductsIsError,
    data: ratableProductsData,
  } = useSelfRatableProductsFeedbackModal({
    queryParams: {
      bypass_viewed: true,
      recent_order_count: recentOrderCount,
    },
  });

  let products: UseRatableItemsReturn['products'] = [];

  if (ratableProductsData?.results) {
    products = ratableProductsData.results.map((product) => {
      return {
        productId: product.id,
        name: product.name,
        type: RatableItemType.Product,
        orderNumber: product.order_id,
        cardImageUrl: product.card_image_url,
        thumbImageUrl: product.thumbnail_image_url,
      };
    });
  }

  return {
    isPending: ratablePairingsIsPending || ratableProductsIsPending,
    // If we only have a list of pairings or products, we should show
    // the user the working list of items.
    isError: ratablePairingsIsError && ratableProductsIsError,
    pairings,
    products,
  };
}
